<template>
	<div class="permissions-container">
		<!-- Header -->
		<div class="permissions-header">
			<div class="header-content">
				<h2 class="permissions-title">Gestión de Permisos</h2>
				
				<!-- Profile Selector Dropdown -->
				<div class="profile-selector-wrapper">
					<div class="dropdown" :class="{ 'show': showProfileDropdown }">
						<button 
							class="btn-profile-selector" 
							type="button" 
							@click="toggleProfileDropdown"
						>
							<div class="selected-profile-info" v-if="selectedProfileId">
								<div class="profile-avatar small" :style="{ backgroundColor: getAvatarColor(selectedProfileName) }">
									{{ getInitials(selectedProfileName) }}
								</div>
								<span class="profile-name-display">{{ selectedProfileName }}</span>
							</div>
							<span v-else class="placeholder-text">Seleccionar perfil...</span>
							<i class="fas fa-chevron-down ml-2"></i>
						</button>
						
						<div class="dropdown-menu profile-dropdown-menu" :class="{ 'show': showProfileDropdown }">
							<div class="dropdown-search">
								<i class="fas fa-search search-icon"></i>
								<input 
									type="text" 
									class="dropdown-search-input" 
									placeholder="Buscar perfil..." 
									v-model="searchProfile"
									ref="profileSearchInput"
								>
							</div>
							<div class="dropdown-items-container">
								<div 
									v-for="profile in filteredProfiles" 
									:key="profile.id"
									class="dropdown-item profile-item"
									:class="{ 'active': selectedProfileId === profile.id }"
									@click="selectProfile(profile)"
								>
									<div class="profile-avatar tiny" :style="{ backgroundColor: getAvatarColor(profile.name) }">
										{{ getInitials(profile.name) }}
									</div>
									<span class="profile-name-text">{{ profile.name }}</span>
								</div>
								<div v-if="filteredProfiles.length === 0" class="dropdown-item text-muted text-center">
									No se encontraron perfiles
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="permissions-content">
			<!-- Main: Permisos del perfil seleccionado -->
			<div class="permissions-main" :class="{ 'no-sidebar': true }">
				<div v-if="selectedProfileId" class="animate-fade-in">
					<!-- Card Header del Perfil -->
					<div class="profile-header-card">
						<div class="profile-header-info">
							<div>
								<h3 class="profile-header-name">{{ selectedProfileName }}</h3>
								<p class="profile-header-desc">Perfil de acceso para {{ selectedProfileName.toLowerCase() }}</p>
							</div>
							<div class="profile-stats">
								<div class="stat-item">
									<span class="stat-value">{{ selectedProfileUsersCount }}</span>
									<span class="stat-label">Usuarios</span>
								</div>
								<div class="stat-item">
									<span class="stat-value">{{ activeModulesCount }}</span>
									<span class="stat-label">Módulos</span>
								</div>
								<div class="stat-item">
									<span class="stat-value">{{ selectedPermissions.length }}</span>
									<span class="stat-label">Permisos</span>
								</div>
							</div>
						</div>
						<button class="btn-save" @click="savePermissions" :disabled="saving">
							<i v-if="saving" class="fas fa-spinner fa-spin"></i>
							<i v-else class="fas fa-save"></i>
							Guardar Cambios
						</button>
					</div>

					<!-- Módulos y Permisos -->
					<div class="modules-section">
						<div class="modules-header">
							<h4 class="modules-title">Módulos configurables</h4>
							<div class="search-box small">
								<i class="fas fa-search search-icon"></i>
								<input type="text" class="search-input" placeholder="Filtrar módulos..." v-model="searchPermission">
							</div>
						</div>

						<div class="modules-list">
							<div v-for="(group, groupName) in filteredPermissions" :key="groupName" class="module-card">
								<div class="module-header">
									<div class="module-icon" :style="{ backgroundColor: getModuleColor(groupName) }">
										<i :class="getModuleIcon(groupName)"></i>
									</div>
									<div class="module-info">
										<span class="module-name">{{ formatGroupName(groupName) }}</span>
										<span class="module-count">{{ getActiveCount(group) }}/{{ group.length }}</span>
									</div>
									<label class="toggle-switch">
										<input type="checkbox" :checked="isGroupActive(group)" @change="toggleGroup(group)">
										<span class="toggle-slider"></span>
									</label>
									<button class="expand-btn" @click="toggleExpand(groupName)">
										<i :class="expandedGroups.includes(groupName) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
									</button>
								</div>
								
								<!-- Permisos expandidos -->
								<div v-if="expandedGroups.includes(groupName)" class="module-permissions">
									<label 
										class="permission-row" 
										v-for="permission in group" 
										:key="permission.id"
									>
										<div class="custom-checkbox">
											<input 
												type="checkbox" 
												:value="permission.id" 
												v-model="selectedPermissions"
											>
											<span class="checkmark">
												<i class="fas fa-check"></i>
											</span>
										</div>
										<span class="permission-text">{{ permission.name }}</span>
									</label>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Estado vacío -->
				<div v-else class="empty-state">
					<div class="empty-icon">
						<i class="fas fa-user-shield"></i>
					</div>
					<h4>Ningún perfil seleccionado</h4>
					<p>Selecciona un perfil en la parte superior para comenzar a editar</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import request from '../../services/request'

export default {
	name: "permissions-table",
	data() {
		return {
			profiles: [],
			permissions: {},
			selectedProfileId: null,
			selectedProfileName: '',
			selectedProfileUsersCount: 0,
			selectedPermissions: [],
			searchProfile: '',
			searchPermission: '',
			saving: false,
			expandedGroups: [],
			showProfileDropdown: false
		}
	},
	computed: {
		filteredProfiles() {
			if (!this.searchProfile) return this.profiles;
			const search = this.searchProfile.toLowerCase();
			return this.profiles.filter(p => p.name.toLowerCase().includes(search));
		},
		filteredPermissions() {
			if (!this.searchPermission) return this.permissions;
			const search = this.searchPermission.toLowerCase();
			const filtered = {};
			for (const [groupName, perms] of Object.entries(this.permissions)) {
				if (
					groupName.toLowerCase().includes(search) || 
					this.getModuleDescription(groupName).toLowerCase().includes(search)
				) {
					filtered[groupName] = perms;
				}
			}
			return filtered;
		},
		activeModulesCount() {
			let count = 0;
			for (const perms of Object.values(this.permissions)) {
				if (perms.some(p => this.selectedPermissions.includes(p.id))) {
					count++;
				}
			}
			return count;
		}
	},
	mounted() {
		this.setHeaderTitle('Permisos');
		this.loadProfiles();
		this.loadPermissions();
		document.addEventListener('click', this.closeDropdownOutside);
	},
	beforeDestroy() {
		document.removeEventListener('click', this.closeDropdownOutside);
	},
	methods: {
		async loadProfiles() {
			try {
				let { data } = await request.get('/profiles?num_items=100');
				if (data && data.data) {
					this.profiles = data.data;
				}
			} catch (error) {
				console.log(error);
			}
		},
		async loadPermissions() {
			try {
				let { data } = await request.get('/catalogue/permissions');
				if (data) {
					this.permissions = data;
				}
			} catch (error) {
				console.log(error);
			}
		},
		toggleProfileDropdown(e) {
			e.stopPropagation();
			this.showProfileDropdown = !this.showProfileDropdown;
			if (this.showProfileDropdown) {
				this.$nextTick(() => {
					this.$refs.profileSearchInput.focus();
				});
			}
		},
		closeDropdownOutside(e) {
			if (this.showProfileDropdown) {
				const dropdown = this.$el.querySelector('.profile-selector-wrapper');
				if (dropdown && !dropdown.contains(e.target)) {
					this.showProfileDropdown = false;
				}
			}
		},
		async selectProfile(profile) {
			this.selectedProfileId = profile.id;
			this.selectedProfileName = profile.name;
			this.selectedProfileUsersCount = profile.users ? profile.users.length : 0;
			this.showProfileDropdown = false;
			this.searchProfile = ''; // Reset search on select
			
			try {
				let { data } = await request.get(`/profiles/${profile.id}`);
				if (data && data.permissions) {
					this.selectedPermissions = data.permissions.map(p => p.id);
				} else {
					this.selectedPermissions = [];
				}
			} catch (error) {
				console.log(error);
			}
		},
		async savePermissions() {
			this.saving = true;
			try {
				let { data } = await request.post(`/profiles/${this.selectedProfileId}`, {
					_method: 'PUT',
					permissions: this.selectedPermissions
				});
				if (data) {
					this.successNotification(data.message);
				}
			} catch (error) {
				if (error.response) {
					await this.errorNotification(error);
				}
			}
			this.saving = false;
		},
		toggleExpand(groupName) {
			const index = this.expandedGroups.indexOf(groupName);
			if (index > -1) {
				this.expandedGroups.splice(index, 1);
			} else {
				this.expandedGroups.push(groupName);
			}
		},
		togglePermission(permissionId) {
			const index = this.selectedPermissions.indexOf(permissionId);
			if (index > -1) {
				this.selectedPermissions.splice(index, 1);
			} else {
				this.selectedPermissions.push(permissionId);
			}
		},
		isGroupActive(group) {
			return group.some(p => this.selectedPermissions.includes(p.id));
		},
		getActiveCount(group) {
			return group.filter(p => this.selectedPermissions.includes(p.id)).length;
		},
		toggleGroup(group) {
			const allSelected = group.every(p => this.selectedPermissions.includes(p.id));
			if (allSelected) {
				this.selectedPermissions = this.selectedPermissions.filter(id => !group.find(p => p.id === id));
			} else {
				group.forEach(p => {
					if (!this.selectedPermissions.includes(p.id)) {
						this.selectedPermissions.push(p.id);
					}
				});
			}
		},
		getInitials(name) {
			if (!name) return '';
			return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
		},
		getAvatarColor(name) {
			if (!name) return '#ccc';
			const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'];
			let hash = 0;
			for (let i = 0; i < name.length; i++) {
				hash = name.charCodeAt(i) + ((hash << 5) - hash);
			}
			return colors[Math.abs(hash) % colors.length];
		},
		getModuleColor(groupName) {
			const colors = {
				'calendar': '#3b82f6',
				'holidays': '#22c55e',
				'users': '#8b5cf6',
				'profiles': '#f97316',
				'permissions': '#6366f1',
				'organization': '#f59e0b',
				'holidays_config': '#10b981',
				'default': '#64748b'
			};
			return colors[groupName] || colors['default'];
		},
		getModuleIcon(groupName) {
			const icons = {
				'calendar': 'fas fa-calendar-alt',
				'holidays': 'fas fa-umbrella-beach',
				'users': 'fas fa-users',
				'profiles': 'fas fa-id-badge',
				'permissions': 'fas fa-shield-alt',
				'organization': 'fas fa-sitemap',
				'holidays_config': 'fas fa-cog',
				'default': 'fas fa-cube'
			};
			return icons[groupName] || icons['default'];
		},
		getModuleDescription(groupName) {
			const descriptions = {
				'calendar': 'Gestión de eventos y programación',
				'holidays': 'Solicitudes y aprobación de vacaciones',
				'holidays_config': 'Configuración de días de vacaciones',
				'users': 'Gestión de usuarios y perfiles',
				'profiles': 'Administración de perfiles y permisos',
				'permissions': 'Gestión de permisos del sistema',
				'organization': 'Estructura organizacional',
				'default': 'Módulo del sistema'
			};
			return descriptions[groupName] || descriptions['default'];
		},
		formatGroupName(name) {
			const translations = {
				'calendar': 'Calendario',
				'holidays': 'Vacaciones',
				'holidays_config': 'Config. Vacaciones',
				'users': 'Usuarios',
				'profiles': 'Perfiles',
				'permissions': 'Permisos',
				'organization': 'Organización'
			};
			return translations[name] || name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ');
		},
		...mapMutations({
			setHeaderTitle: 'setHeaderTitle',
		}),
		...mapActions({
			errorNotification: 'alert/error',
			successNotification: 'alert/success',
		})
	}
}
</script>

<style scoped>
.permissions-container {
	background: #f8fafc;
	min-height: calc(100vh - 80px); /* Ajuste para header global */
	display: flex;
	flex-direction: column;
}

/* Header Rediseñado */
.permissions-header {
	padding: 1rem 2rem;
	background: white;
	border-bottom: 1px solid #e2e8f0;
	position: sticky;
	top: 0;
	z-index: 100;
}

.header-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
	max-width: 1200px;
	margin: 0 auto;
}

.permissions-title {
	font-size: 1.25rem;
	font-weight: 700;
	color: #1e293b;
	margin: 0;
}

/* Profile Selector Dropdown */
.profile-selector-wrapper {
	position: relative;
	min-width: 280px;
}

.btn-profile-selector {
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background: white;
	border: 1px solid #cbd5e1;
	border-radius: 8px;
	padding: 0.5rem 1rem;
	cursor: pointer;
	transition: all 0.2s;
	outline: none;
}

.btn-profile-selector:hover,
.btn-profile-selector:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.selected-profile-info {
	display: flex;
	align-items: center;
	gap: 10px;
}

.profile-avatar.small {
	width: 28px;
	height: 28px;
	font-size: 0.75rem;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-weight: 600;
}

.profile-name-display {
	font-weight: 600;
	color: #334155;
	font-size: 0.9rem;
}

.placeholder-text {
	color: #64748b;
}

.profile-dropdown-menu {
	position: absolute;
	top: 100%;
	left: 0;
	width: 100%;
	margin-top: 4px;
	background: white;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
	z-index: 50;
	padding: 0.5rem;
	display: none;
}

.profile-dropdown-menu.show {
	display: block;
}

.dropdown-search {
	position: relative;
	margin-bottom: 0.5rem;
}

.dropdown-search-input {
	width: 100%;
	padding: 0.5rem 0.5rem 0.5rem 2rem;
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	font-size: 0.85rem;
	outline: none;
}

.dropdown-search-input:focus {
	border-color: #3b82f6;
}

.dropdown-search .search-icon {
	position: absolute;
	left: 10px;
	top: 50%;
	transform: translateY(-50%);
	color: #94a3b8;
	font-size: 0.8rem;
}

.dropdown-items-container {
	max-height: 300px;
	overflow-y: auto;
}

.dropdown-item.profile-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0.5rem 0.75rem;
	border-radius: 6px;
	cursor: pointer;
	transition: background 0.1s;
}

.dropdown-item:hover {
	background-color: #f1f5f9;
}

.dropdown-item.active {
	background-color: #eff6ff;
	color: #1d4ed8;
}

.profile-avatar.tiny {
	width: 24px;
	height: 24px;
	font-size: 0.7rem;
	border-radius: 5px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-weight: 600;
	flex-shrink: 0;
}

/* Main Content */
.permissions-content {
	flex: 1;
	display: flex;
	justify-content: center;
	padding: 2rem;
}

.permissions-main {
	width: 100%;
	max-width: 1000px;
}

.profile-header-card {
	background: white;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	padding: 1.5rem 2rem;
	margin-bottom: 1.5rem;
	display: flex;
	justify-content: space-between;
	align-items: center;
	box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.profile-header-name {
	font-size: 1.25rem;
	font-weight: 700;
	color: #1e293b;
	margin: 0;
}

.profile-header-desc {
	color: #64748b;
	font-size: 0.85rem;
	margin: 0.25rem 0 1rem;
}

.profile-stats {
	display: flex;
	gap: 2rem;
}

.stat-item {
	display: flex;
	flex-direction: column;
}

.stat-value {
	font-size: 1.1rem;
	font-weight: 700;
	color: #1e293b;
}

.stat-label {
	font-size: 0.75rem;
	color: #64748b;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.btn-save {
	background: #10b981;
	color: white;
	border: none;
	padding: 0.625rem 1.25rem;
	border-radius: 6px;
	font-weight: 500;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 8px;
	transition: all 0.2s;
	box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.btn-save:hover {
	background: #059669;
	transform: translateY(-1px);
}

.btn-save:disabled {
	opacity: 0.7;
	cursor: not-allowed;
	transform: none;
}

/* Modules Section */
.modules-section {
	background: transparent;
}

.modules-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1rem;
}

.modules-title {
	font-size: 1rem;
	font-weight: 600;
	color: #1e293b;
	margin: 0;
}

.module-card {
	background: white;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	margin-bottom: 0.75rem;
	overflow: hidden;
	box-shadow: 0 1px 2px rgba(0,0,0,0.02);
}

.module-header {
	display: flex;
	align-items: center;
	padding: 0.75rem 1rem;
	gap: 1rem;
}

.module-icon {
	width: 36px;
	height: 36px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 0.9rem;
}

.module-info {
	flex: 1;
	display: flex;
	align-items: center;
	gap: 10px;
}

.module-name {
	font-weight: 600;
	color: #334155;
	font-size: 0.9rem;
}

.module-count {
	font-size: 0.7rem;
	color: #64748b;
	background: #f1f5f9;
	padding: 2px 6px;
	border-radius: 4px;
	font-weight: 600;
}

/* Toggle Switch */
.toggle-switch {
	position: relative;
	width: 40px;
	height: 22px;
	margin: 0 0.5rem;
}

.toggle-switch input {
	opacity: 0;
	width: 0;
	height: 0;
}

.toggle-slider {
	position: absolute;
	cursor: pointer;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: #cbd5e1;
	transition: 0.3s;
	border-radius: 24px;
}

.toggle-slider:before {
	position: absolute;
	content: "";
	height: 18px;
	width: 18px;
	left: 2px;
	bottom: 2px;
	background-color: white;
	transition: 0.3s;
	border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
	background-color: #10b981;
}

.toggle-switch input:checked + .toggle-slider:before {
	transform: translateX(18px);
}

.expand-btn {
	background: none;
	border: none;
	color: #94a3b8;
	cursor: pointer;
	padding: 0.25rem;
	transition: color 0.2s;
}

.expand-btn:hover {
	color: #475569;
}

.module-permissions {
	padding: 0.25rem 1rem 0.25rem;
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 0 1.5rem;
	border-top: 1px solid #f8fafc;
	background: #fcfcfc;
}

.permission-row {
	display: flex;
	align-items: center;
	padding: 8px 0;
	margin: 0;
	cursor: pointer;
	border-bottom: 1px dashed #f1f5f9;
}

.permission-row:nth-last-child(-n+2) {
	border-bottom: none;
}

.permission-row:hover .custom-checkbox {
	border-color: #10b981;
}

.custom-checkbox {
	position: relative;
	width: 20px;
	height: 20px;
	margin-right: 12px;
	border: 2px solid #cbd5e1;
	border-radius: 6px;
	background: white;
	transition: all 0.2s;
	display: flex;
	align-items: center;
	justify-content: center;
}

.custom-checkbox input {
	position: absolute;
	opacity: 0;
	cursor: pointer;
	height: 0;
	width: 0;
}

.checkmark {
	color: white;
	font-size: 10px;
	display: none;
}

.custom-checkbox:has(input:checked) {
	background-color: #10b981;
	border-color: #10b981;
}

.custom-checkbox:has(input:checked) .checkmark {
	display: block;
}

.permission-text {
	font-size: 0.85rem;
	color: #475569;
}

.search-box.small {
	position: relative;
	width: 250px;
}

.search-box .search-input {
	width: 100%;
	padding: 0.4rem 0.5rem 0.4rem 2rem;
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	font-size: 0.85rem;
}

.search-box .search-icon {
	position: absolute;
	left: 10px;
	top: 50%;
	transform: translateY(-50%);
	color: #94a3b8;
	font-size: 0.8rem;
}

@media (max-width: 768px) {
	.header-content {
		flex-direction: column;
		gap: 1rem;
		align-items: flex-start;
	}

	.profile-selector-wrapper {
		width: 100%;
	}

	.permissions-header {
		position: static;
	}

	.module-permissions {
		grid-template-columns: 1fr;
	}

	.profile-header-card {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.btn-save {
		width: 100%;
		justify-content: center;
	}
}
</style>
